import React, { useState } from 'react';
import { ShieldCheck, X, Building2 } from 'lucide-react';
import styles from './DomainRestrictionField.module.css';

// Keep this in sync with the backend normalizer (utils/emailDomain.ts).
function normalizeDomain(input: string): string {
  if (!input) return '';
  let s = input.trim().toLowerCase();
  s = s.replace(/^[a-z]+:\/\//, '').split('/')[0];
  const at = s.lastIndexOf('@');
  if (at !== -1) s = s.slice(at + 1);
  s = s.replace(/^@+/, '').replace(/^\.+/, '').replace(/\.+$/, '');
  return s;
}

const DOMAIN_RE =
  /^(?=.{1,253}$)[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)+$/;

interface DomainRestrictionFieldProps {
  value: string[];
  onChange: (domains: string[]) => void;
}

/**
 * Lets a creator limit album access to one or more email domains. Renders a
 * short explanation of why the control exists, a worked example, and a chip
 * input. Shared by the album creation wizard and the album settings modal.
 */
export function DomainRestrictionField({ value, onChange }: DomainRestrictionFieldProps) {
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);

  const commit = () => {
    const tokens = draft.split(/[\s,]+/).filter(Boolean);
    if (tokens.length === 0) {
      setDraft('');
      return;
    }
    const next = [...value];
    const invalid: string[] = [];
    for (const tok of tokens) {
      const d = normalizeDomain(tok);
      if (!d) continue;
      if (!DOMAIN_RE.test(d)) {
        invalid.push(tok.trim());
        continue;
      }
      if (!next.includes(d)) next.push(d);
    }
    onChange(next);
    if (invalid.length > 0) {
      setError(`Not a valid domain: ${invalid.join(', ')}`);
      setDraft(invalid.join(', '));
    } else {
      setError(null);
      setDraft('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      commit();
    } else if (e.key === 'Backspace' && draft === '' && value.length > 0) {
      // Backspace on an empty input removes the last chip.
      onChange(value.slice(0, -1));
    }
  };

  const removeDomain = (domain: string) => onChange(value.filter((d) => d !== domain));

  return (
    <div className={styles.field}>
      {/* Why this exists */}
      <div className={styles.explainer}>
        <div className={styles['explainer-icon']}>
          <ShieldCheck size={16} />
        </div>
        <div className={styles['explainer-body']}>
          <p className={styles['explainer-title']}>Restrict access by email domain</p>
          <p className={styles['explainer-text']}>
            By default anyone with the invite link can join. Add one or more domains to
            limit access to your organization — useful when a company wants only its own
            employees to see the photos. Guests signing in with an email outside these
            domains are blocked from joining.
          </p>
          <div className={styles.example}>
            <Building2 size={13} className={styles['example-icon']} />
            <span>
              Example: add <code className={styles.code}>acme.com</code> so only people with an{' '}
              <code className={styles.code}>@acme.com</code> address (e.g.{' '}
              <code className={styles.code}>jane@acme.com</code>) can access this album.
            </span>
          </div>
        </div>
      </div>

      {/* Chip input */}
      <label className={styles.label}>Allowed email domains (optional)</label>
      <div className={styles['chip-input']}>
        {value.map((domain) => (
          <span key={domain} className={styles.chip}>
            @{domain}
            <button
              type="button"
              onClick={() => removeDomain(domain)}
              className={styles['chip-remove']}
              aria-label={`Remove ${domain}`}
            >
              <X size={12} strokeWidth={2.5} />
            </button>
          </span>
        ))}
        <input
          type="text"
          inputMode="email"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            if (error) setError(null);
          }}
          onKeyDown={handleKeyDown}
          onBlur={commit}
          placeholder={value.length === 0 ? 'e.g. acme.com — press Enter to add' : 'Add another…'}
          className={styles.input}
          aria-label="Add an allowed email domain"
        />
      </div>

      {error ? (
        <p className={styles.error}>{error}</p>
      ) : value.length > 0 ? (
        <p className={styles.hint}>
          Only people with an email ending in{' '}
          {value.map((d, i) => (
            <React.Fragment key={d}>
              {i > 0 && (i === value.length - 1 ? ' or ' : ', ')}
              <strong>@{d}</strong>
            </React.Fragment>
          ))}{' '}
          can join.
        </p>
      ) : (
        <p className={styles.hint}>Leave empty to let anyone with the link join.</p>
      )}
    </div>
  );
}
